import { SHIPPING_OPTIONS } from './components/advanced-shipping-notice-drawer/steps/constants';

export const SubmittedAsnStatuses = {
  InTransit: 'InTransit',
  Received: 'Received',
  Incomplete: 'Incomplete',
  // The following are only here as ShipBob supports them but we do nothing with them for now.
  Awaiting: 'Awaiting',
  Processing: 'Processing',
  Arrived: 'Arrived',
  PartiallyArrived: 'PartiallyArrived',
  PartiallyArrivedAtHub: 'PartiallyArrivedAtHub',
  ArrivedAtHub: 'ArrivedAtHub',
  ProcessingAtHub: 'ProcessingAtHub',
  InternalTransfer: 'InternalTransfer',
};

export const CompletedAsnStatuses = {
  Cancelled: 'Cancelled',
  Completed: 'Completed',
};
export const AsnStatus = {
  Draft: 'Draft',
  ...SubmittedAsnStatuses,
  ...CompletedAsnStatuses,
};

export const AsnStatusList = Object.values(AsnStatus);

/**
 * @typedef {object} AsnProduct
 * @property {string} productId
 * @property {string} name
 * @property {string} sku
 * @property {string|null} lotNumber
 * @property {DateTime} expirationDate
 * @property {object} productIdentifiers
 * @property {number} quantity
 * @property {number} quantityPerBox - Number of products per box ( used for defining shipments )
 * @property {string} uniqueProductRowId - Unique identifier for each product row
 */
export const initialProductDefaults = {
  productId: '',
  name: '',
  sku: '',
  lotNumber: undefined,
  expirationDate: undefined,
  productIdentifiers: {},
  quantity: 0,
  uniqueProductRowId: '',
};

/**
 * @typedef {object} AsnBox
 * @property {string} boxId - GUID
 * @property {string} boxConfigurationId - GUID
 * @property {string} trackingNumber
 * @property {Array<AsnProduct>} products
 * @property {object} shippingConfiguration - Should match that of a CSW object
 * @property {number} boxesPerConfig - Number of boxes per configuration
 */
export const initialBoxDefaults = {
  boxId: '',
  boxConfigurationId: '',
  trackingNumber: '',
  products: [],
  shippingConfiguration: null,
  boxesPerConfig: 1,
};

/**
 * @typedef {object} AsnContact
 * @property {string} name
 * @property {string} email
 * @property {string} phone
 */
export const initialContactDefaults = {
  name: '',
  email: '',
  phone: '',
};

/**
 * @typedef {object} AsnFreightConfiguration
 * @property {string} confirmedDropOffDate
 * @property {AsnContact} carrierContact
 * @property {AsnContact} tenantContact
 * @property {string} deliveryNote
 * @property {string} freightCarrier
 * @property {DateTime} dropOffOption1
 * @property {DateTime} dropOffOption2
 * @property {DateTime} dropOffOption3
 */
export const initialFreightConfigurationDefaults = {
  dropOffOption1: undefined,
  dropOffOption2: undefined,
  dropOffOption3: undefined,
  confirmedDropOffDate: null,
  carrierContact: initialContactDefaults,
  tenantContact: initialContactDefaults,
  deliveryNote: '',
  freightCarrier: '',
};

/**
 * @typedef {object} AdvancedShippingNotice
 * @property {string} asnId - GUID of ASN as defined by FDS
 * @property {string} partnerAsnId
 * @property {string} receivingCenter - Warehouse this ASN is expected to go to
 * @property {string} clientId
 * @property {DateTime} expectedArrivalDate
 * @property {('Palet'|'Parcel'|'FloorLoadedContainer')} packageType - How the ASN is shipped
 * @property {DateTime} createdAt
 * @property {DateTime} modifiedAt
 * @property {string} status - As defined by the FDS, readonly
 * @property {string} purchaseOrderNumber
 * @property {Array.<AsnBox>} boxes
 * @property {Array.<AsnExpectedProduct>} expectedProducts
 * @property {AsnFreightConfiguration} freightConfiguration
 * @property {object} parcelConfigurations
 * @property {boolean} hasBoxLabels
 * @property {DateTime} submittedAt
 */
export const initialAdvancedShippingNotice = {
  asnId: '',
  partnerAsnId: '',
  receivingCenter: '',
  clientId: '',
  expectedArrivalDate: '',
  packageType: SHIPPING_OPTIONS.parcel,
  createdAt: undefined,
  modifiedAt: undefined,
  submittedAt: undefined,
  status: AsnStatus.Draft,
  purchaseOrderNumber: '',
  boxes: [],
  expectedProducts: [],
  freightConfiguration: initialFreightConfigurationDefaults,
  parcelConfigurations: [],
  hasBoxLabels: false,
};

/**
 * @typedef {object} AsnExpectedProduct
 * @property {string} productId
 * @property {object} productIdentifiers - Dictionary of product identifiers
 * @property {string} sku
 * @property {string} lotNumber
 * @property {string} name
 * @property {string} expirationDate
 * @property {number} quantity
 * @property {number} receivedQuantity
 * @property {number} stowedQuantity
 */
